import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode, RefObject } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import CommandPalette, { type StudioCommand } from '../lyrics-studio/CommandPalette'
import { Icon } from '../album-studio/icons'
import { Kbd } from '../album-studio/primitives'
import { ToastView, useToastHost, type FlashFn } from '../album-studio/Toast'
import { useAuth } from '../../../context/AuthContext'
import { ContentModeProvider, useContentMode } from './content-mode'

/**
 * SPEC-031 Admin Console — the `.album-studio`-scoped "Studio Console" shell
 * (ADR-084). It replaces the old indigo/gray admin chrome: a quiet left
 * {@link SideNav}, a top bar with a route breadcrumb + ⌘K trigger + static-mode
 * indicator, the reused ⌘K {@link CommandPalette}, a bottom toast, and the
 * `<Outlet/>` host — all inside the warm-gold token scope every later admin
 * surface inherits. Translated from the signed-off `admin-shell.jsx` prototype;
 * the design's fake `StateSwitch` is intentionally dropped (surfaces render the
 * four states from real fetch status), and the mock user is the real
 * {@link useAuth} identity.
 */

interface NavItem {
  id: string
  label: string
  path: string
  icon: (props: Record<string, unknown>) => JSX.Element
  /** When true, only an exact path match is active (the Dashboard index). */
  exact?: boolean
}

/** The console's primary navigation, in the signed-off order (Dashboard, Albums, …). Catalog (TASK-3022) sits between Albums and Library. */
const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', path: '/admin', icon: Icon.grid, exact: true },
  { id: 'albums', label: 'Albums', path: '/admin/albums', icon: Icon.disc },
  { id: 'catalog', label: 'Catalog', path: '/admin/catalog', icon: Icon.music },
  { id: 'library', label: 'Library', path: '/admin/library', icon: Icon.image },
  { id: 'characters', label: 'Characters', path: '/admin/characters', icon: Icon.users },
  { id: 'releases', label: 'Releases', path: '/admin/releases', icon: Icon.rocket },
  { id: 'links', label: 'Links', path: '/admin/links', icon: Icon.link },
  { id: 'press', label: 'Press', path: '/admin/press', icon: Icon.press },
  { id: 'signal', label: 'Signal', path: '/admin/signal', icon: Icon.bolt },
  { id: 'users', label: 'Users', path: '/admin/users', icon: Icon.users },
  { id: 'settings', label: 'Settings', path: '/admin/settings', icon: Icon.gear },
]

/** Whether a nav item is active for the current pathname (prefix match, exact for the index). */
function navItemActive(pathname: string, item: NavItem): boolean {
  if (item.exact) return pathname === item.path
  return pathname === item.path || pathname.startsWith(`${item.path}/`)
}


interface ConsoleContextValue {
  /** Show a toast (2.6s; 30s when it carries actions such as Undo). */
  flash: FlashFn
  /**
   * The console's global ⌘K commands (area navigation + primary create actions).
   * A mounted `AlbumStudioFrame` merges these beneath its own contextual commands
   * so the single studio palette exposes both global and local commands.
   */
  globalCommands: StudioCommand[]
  /**
   * Register a mounted studio frame so the console yields ⌘K — and closes its own
   * palette — to it. This is the fix for a single keystroke opening two stacked
   * palettes on studio surfaces. Ref-counted; returns an unregister function to
   * call on the frame's unmount.
   */
  registerStudioFrame: () => () => void
}

const ConsoleContext = createContext<ConsoleContextValue | null>(null)

/** Access the console shell's toast host from a surface rendered in the `<Outlet/>`. */
export function useConsole(): ConsoleContextValue {
  const ctx = useContext(ConsoleContext)
  if (!ctx) throw new Error('useConsole must be used within <AdminConsoleShell>')
  return ctx
}

/**
 * Access the console shell if it is present, or `null` when the caller is
 * rendered outside it (e.g. an `AlbumStudioFrame` mounted standalone in a unit
 * test). Unlike {@link useConsole}, this never throws.
 *
 * @returns The console context, or `null` when there is no `<AdminConsoleShell>` ancestor.
 */
export function useOptionalConsole(): ConsoleContextValue | null {
  return useContext(ConsoleContext)
}

/* -------------------------------------------------------------------------- */
/* Left rail                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The rail's inner content: brand lockup, primary nav with the gold active
 * indicator (react-router `<Link>` + `useLocation`), the ⌘K hint, and the
 * signed-in user card with sign-out. Shared verbatim by the desktop
 * {@link SideNav} and the mobile {@link NavDrawer} so the two never drift.
 * Keyboard-operable with gold focus rings.
 *
 * @param pathname - The active route, for the gold active-item indicator.
 * @param onLogout - Sign the current user out.
 * @param onNavigate - Optional callback fired when a nav item is selected; the
 *   drawer passes its close handler here so a selection dismisses the overlay.
 */
function RailBody({ pathname, onLogout, onNavigate }: { pathname: string; onLogout: () => void; onNavigate?: () => void }): JSX.Element {
  const { user } = useAuth()
  const name = user?.name ?? user?.email ?? 'Signed in'
  const initial = (user?.name ?? user?.email ?? 'A').trim().charAt(0).toUpperCase()

  return (
    <>
      <div className="flex items-center gap-2.5 px-2 pb-5 pt-0.5">
        <div className="grid h-[34px] w-[34px] place-items-center rounded-[10px] bg-[var(--as-ink)] font-display text-[19px] leading-none text-white">tc</div>
        <div className="leading-[1.15]">
          <div className="font-display text-[17px] tracking-[-0.01em] text-[var(--as-ink)]">Music Hub</div>
          <div className="text-[10.5px] tracking-[0.04em] text-[var(--as-ink-3)]">STUDIO CONSOLE</div>
        </div>
      </div>

      <div className="grid gap-0.5">
        {NAV_ITEMS.map((item) => {
          const active = navItemActive(pathname, item)
          const NavIcon = item.icon
          return (
            <Link
              key={item.id}
              to={item.path}
              onClick={onNavigate}
              aria-current={active ? 'page' : undefined}
              className={`flex items-center gap-[11px] rounded-[10px] border px-[11px] py-[9px] text-[13.5px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--as-gold)] ${
                active
                  ? 'border-[var(--as-line)] bg-[var(--as-panel)] text-[var(--as-ink)] shadow-[var(--as-shadow-sm)]'
                  : 'border-transparent text-[var(--as-ink-2)] hover:bg-black/[0.045]'
              }`}
            >
              <span className={`relative grid w-5 place-items-center text-[17px] ${active ? 'text-[var(--as-gold-600)]' : 'text-[var(--as-ink-3)]'}`}>
                {active && <span aria-hidden="true" className="absolute -left-[13px] h-[18px] w-[3px] rounded-sm bg-[var(--as-gold)]" />}
                <NavIcon />
              </span>
              <span className="flex-1">{item.label}</span>
            </Link>
          )
        })}
      </div>

      <div className="mt-auto px-2 pb-0.5 pt-3.5">
        <div className="mb-2.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--as-ink-3)]">Shortcuts</div>
        <div className="flex items-center gap-1.5 text-[12px] text-[var(--as-ink-2)]">
          <Kbd>⌘</Kbd>
          <Kbd>K</Kbd>
          <span className="text-[var(--as-ink-3)]">command palette</span>
        </div>
        <div className="mt-4 flex items-center gap-2.5 rounded-[11px] border border-[var(--as-line)] bg-[var(--as-panel)] px-2.5 py-2.5">
          <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[var(--as-gold-tint)] font-display text-[14px] text-[var(--as-gold-600)]">{initial}</div>
          <div className="min-w-0 leading-[1.2]">
            <div className="truncate text-[12.5px] font-semibold text-[var(--as-ink)]">{name}</div>
            <button
              type="button"
              onClick={onLogout}
              className="rounded text-[10.5px] text-[var(--as-ink-3)] transition-colors hover:text-[var(--as-gold-600)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--as-gold)]"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * The desktop left navigation rail (≥md). Hidden below the `md` breakpoint,
 * where {@link NavDrawer} presents the same {@link RailBody} as an overlay. At
 * ≥md this is byte-identical to the pre-collapse rail.
 *
 * @param pathname - The active route, for the active-item indicator.
 * @param onLogout - Sign the current user out.
 */
function SideNav({ pathname, onLogout }: { pathname: string; onLogout: () => void }): JSX.Element {
  return (
    <nav aria-label="Primary" className="hidden w-[228px] shrink-0 flex-col border-r border-[var(--as-line)] bg-[var(--as-rail)] px-3.5 py-[18px] md:flex">
      <RailBody pathname={pathname} onLogout={onLogout} />
    </nav>
  )
}

/**
 * The mobile navigation drawer (below md): the {@link RailBody} rail content in
 * a left-sliding overlay behind a scrim. Restrained ~240ms transitions (the
 * admin family's motion idiom — transform/opacity, no bounce); closes on scrim
 * click, Esc, after any nav selection, and when the viewport crosses to the
 * desktop breakpoint mid-open (the rail takes over). Focus moves into the panel
 * on open and is trapped within it (Tab wraps first↔last); the caller's
 * `onClose` returns focus to the trigger. Body scroll is locked while open and
 * the closed panel is `inert`, so its links stay out of the tab order and the
 * accessibility tree.
 *
 * @param open - Whether the drawer is open.
 * @param onClose - Close the drawer (also restores focus to the trigger).
 * @param pathname - The active route, for the rail's active-item indicator.
 * @param onLogout - Sign the current user out.
 */
function NavDrawer({ open, onClose, pathname, onLogout }: { open: boolean; onClose: () => void; pathname: string; onLogout: () => void }): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const panel = panelRef.current
    if (!open) {
      panel?.setAttribute('inert', '')
      return undefined
    }
    panel?.removeAttribute('inert')
    panel?.focus()

    // Esc closes; Tab is trapped within the panel (first↔last wrap), matching the
    // Visual Studio AssetDrawer's modal pattern so keyboard focus can't escape
    // behind the scrim while the overlay is open.
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key !== 'Tab' || !panel) return
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      )
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKey)

    // Crossing to the desktop breakpoint mid-open hands navigation back to the
    // rail, so close the drawer (and release the scroll lock) rather than leaving
    // an orphaned overlay hidden behind `md:hidden`.
    const desktopMq = window.matchMedia('(min-width: 768px)')
    const onDesktop = (e: MediaQueryListEvent): void => {
      if (e.matches) onClose()
    }
    desktopMq.addEventListener('change', onDesktop)

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      desktopMq.removeEventListener('change', onDesktop)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  return (
    <div className="md:hidden">
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`fixed inset-0 z-[80] bg-[var(--as-ink)]/40 transition-opacity duration-[240ms] ease-out ${open ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      />
      <div
        ref={panelRef}
        id="console-nav-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Studio console navigation"
        tabIndex={-1}
        className={`fixed inset-y-0 left-0 z-[90] flex w-[260px] flex-col border-r border-[var(--as-line)] bg-[var(--as-rail)] px-3.5 py-[18px] shadow-[var(--as-shadow-lg)] transition-transform duration-[240ms] ease-out focus:outline-none ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <RailBody pathname={pathname} onLogout={onLogout} onNavigate={onClose} />
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Top bar                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The mobile chrome header (below md): a slim bar carrying the drawer trigger
 * and the "Music Hub / STUDIO CONSOLE" wordmark (the rail header's type
 * treatment, scaled down), and nothing else. It replaces the desktop
 * {@link TopBar} below md, where the 228px rail and its breadcrumb/⌘K controls
 * are unavailable. The trigger is a composed three-bar glyph (the icon set has
 * no menu glyph); `aria-expanded` + `aria-controls` wire it to {@link NavDrawer}.
 *
 * @param navOpen - Whether the drawer is currently open (drives `aria-expanded`).
 * @param onOpenNav - Open the navigation drawer.
 * @param triggerRef - Ref to the trigger, so the drawer can restore focus on close.
 */
function MobileTopBar({ navOpen, onOpenNav, triggerRef }: { navOpen: boolean; onOpenNav: () => void; triggerRef: RefObject<HTMLButtonElement> }): JSX.Element {
  return (
    <header className="flex h-[54px] shrink-0 items-center gap-3 border-b border-[var(--as-line)] bg-[var(--as-panel)] px-4 md:hidden">
      <button
        ref={triggerRef}
        type="button"
        onClick={onOpenNav}
        aria-label="Open navigation menu"
        aria-haspopup="dialog"
        aria-expanded={navOpen}
        aria-controls="console-nav-drawer"
        className="grid h-9 w-9 shrink-0 place-items-center rounded-[9px] border border-[var(--as-line)] bg-[var(--as-rail)] transition-colors hover:border-[#d9cfbc] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--as-gold)]"
      >
        <span aria-hidden="true" className="flex w-[18px] flex-col gap-[3.5px]">
          <span className="h-[1.8px] w-full rounded-full bg-[var(--as-ink-2)]" />
          <span className="h-[1.8px] w-full rounded-full bg-[var(--as-ink-2)]" />
          <span className="h-[1.8px] w-full rounded-full bg-[var(--as-ink-2)]" />
        </span>
      </button>

      <div className="flex min-w-0 items-center gap-2">
        <div className="grid h-[28px] w-[28px] shrink-0 place-items-center rounded-[8px] bg-[var(--as-ink)] font-display text-[15px] leading-none text-white">tc</div>
        <div className="min-w-0 leading-[1.12]">
          <div className="truncate font-display text-[15px] tracking-[-0.01em] text-[var(--as-ink)]">Music Hub</div>
          <div className="text-[9.5px] tracking-[0.04em] text-[var(--as-ink-3)]">STUDIO CONSOLE</div>
        </div>
      </div>
    </header>
  )
}

/**
 * The desktop top bar (≥md): a route-derived breadcrumb, an optional static-mode
 * indicator (D3), and the ⌘K command-palette trigger. Hidden below md, where
 * {@link MobileTopBar} takes its place. The mode indicator is labelled text
 * (never colour alone) so it satisfies WCAG 1.4.1. The trigger hides when a
 * studio frame owns ⌘K — that surface renders its own palette trigger in the
 * frame breadcrumb bar, so keeping the console's would be a dead second control.
 *
 * @param activeLabel - The active nav area label (breadcrumb tail).
 * @param readOnly - Static mode: show the read-only indicator.
 * @param hideCmdTrigger - Hide the ⌘K trigger (a studio frame owns the palette).
 * @param onOpenCmd - Open the console command palette.
 */
function TopBar({ activeLabel, readOnly, hideCmdTrigger, onOpenCmd }: { activeLabel: string; readOnly: boolean; hideCmdTrigger: boolean; onOpenCmd: () => void }): JSX.Element {
  return (
    <header className="hidden h-[58px] shrink-0 items-center justify-between gap-5 border-b border-[var(--as-line)] bg-[var(--as-panel)] px-[22px] md:flex">
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2 overflow-hidden">
        <Link
          to="/admin"
          className="whitespace-nowrap rounded text-[13.5px] font-medium text-[var(--as-ink-3)] transition-colors hover:text-[var(--as-gold-600)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--as-gold)]"
        >
          Music Hub
        </Link>
        <span aria-hidden="true" className="flex text-xs text-[var(--as-ink-3)]">
          <Icon.chev />
        </span>
        <span className="whitespace-nowrap text-[13.5px] font-semibold text-[var(--as-ink)]" aria-current="page">
          {activeLabel}
        </span>
      </nav>

      <div className="flex shrink-0 items-center gap-3.5">
        {readOnly && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--as-amber-tint)] bg-[var(--as-amber-tint)] px-2.5 py-1 text-[11px] font-semibold text-[var(--as-amber)]">
            <Icon.box style={{ fontSize: 12 }} />
            Static mode · read-only
          </span>
        )}
        {!hideCmdTrigger && (
          <button
            type="button"
            onClick={onOpenCmd}
            title="Command palette (⌘K)"
            aria-label="Open command palette"
            className="inline-flex w-[220px] items-center gap-2 rounded-[9px] border border-[var(--as-line)] bg-[var(--as-rail)] py-1.5 pl-[11px] pr-[10px] font-[family-name:var(--as-font-ui)] text-[12.5px] text-[var(--as-ink-3)] transition-colors hover:border-[#d9cfbc] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--as-gold)]"
          >
            <Icon.search style={{ fontSize: 14 }} />
            <span className="flex-1 text-left">Search releases, links…</span>
            <span className="flex gap-1">
              <Kbd>⌘</Kbd>
              <Kbd>K</Kbd>
            </span>
          </button>
        )}
      </div>
    </header>
  )
}

/* -------------------------------------------------------------------------- */
/* Shell                                                                        */
/* -------------------------------------------------------------------------- */

/** The inner shell (inside the ContentMode provider) — owns ⌘K, toast, and the frame. */
function ConsoleFrame({ children }: { children: ReactNode }): JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { readOnly } = useContentMode()

  const [paletteOpen, setPaletteOpen] = useState(false)
  const toastHost = useToastHost()
  const { flash } = toastHost

  // The mobile nav drawer (below md). The trigger ref lets the drawer return
  // focus to the menu button when it closes (Esc, scrim, or a nav selection).
  const [navOpen, setNavOpen] = useState(false)
  const navTriggerRef = useRef<HTMLButtonElement>(null)
  const openNav = useCallback(() => setNavOpen(true), [])
  const closeNav = useCallback(() => {
    setNavOpen(false)
    navTriggerRef.current?.focus()
  }, [])
  // Count of mounted studio frames. While ≥1 is mounted, that frame owns ⌘K and
  // its own palette (which merges these global commands), so the console yields:
  // its ⌘K listener no-ops, its palette stays closed, and its trigger hides.
  const [studioFrameCount, setStudioFrameCount] = useState(0)
  const studioFrameActive = studioFrameCount > 0

  const registerStudioFrame = useCallback((): (() => void) => {
    setStudioFrameCount((c) => c + 1)
    return () => setStudioFrameCount((c) => Math.max(0, c - 1))
  }, [])


  // ⌘K / Ctrl+K toggles the command palette — the keyboard-first fast lane. When
  // a studio frame is mounted it owns ⌘K, so this listener yields (the frame's
  // palette holds both the global and the contextual commands).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        if (studioFrameActive) return
        e.preventDefault()
        setPaletteOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [studioFrameActive])

  // Close the console palette when a studio frame takes over ⌘K, so an
  // already-open palette doesn't spring back open when that frame later unmounts
  // (reachable via back/forward navigation off a studio surface).
  useEffect(() => {
    if (studioFrameActive) setPaletteOpen(false)
  }, [studioFrameActive])

  const handleLogout = useCallback(() => {
    void logout().then(() => navigate('/'))
  }, [logout, navigate])

  const activeItem = useMemo(() => NAV_ITEMS.find((n) => navItemActive(location.pathname, n)) ?? NAV_ITEMS[0], [location.pathname])

  // ⌘K commands: jump to any surface + the primary create actions. Create actions
  // are disabled in static (read-only) mode, matching the server's CMS_ONLY gate.
  const commands = useMemo<StudioCommand[]>(() => {
    const list: StudioCommand[] = []
    NAV_ITEMS.forEach((n) =>
      list.push({ id: `nav-${n.id}`, group: 'Go to', label: n.label, hint: `Open the ${n.label} surface`, run: () => navigate(n.path) }),
    )
    const actions: { id: string; label: string; hint: string; to: string }[] = [
      { id: 'new-release', label: 'New release', hint: 'Create a single, EP, or album entry', to: '/admin/releases?new=true' },
      { id: 'new-album', label: 'New album', hint: 'Open Album Studio', to: '/admin/albums' },
      { id: 'new-song', label: 'New song', hint: 'A title is enough; no album needed', to: '/admin/catalog?new=1' },
      { id: 'add-link', label: 'Add platform link', hint: 'Outbound streaming link', to: '/admin/links?new=true' },
      { id: 'add-press', label: 'Add press item', hint: 'Article, quote, or media kit', to: '/admin/press?new=true' },
      { id: 'edit-settings', label: 'Edit settings', hint: 'Site info + theme', to: '/admin/settings' },
    ]
    actions.forEach((a) =>
      list.push({
        id: `act-${a.id}`,
        group: 'Actions',
        label: a.label,
        hint: readOnly && a.id !== 'new-album' && a.id !== 'edit-settings' ? 'Unavailable in static mode' : a.hint,
        disabled: readOnly && a.id !== 'new-album' && a.id !== 'edit-settings',
        run: () => navigate(a.to),
      }),
    )
    return list
  }, [navigate, readOnly])

  return (
    <ConsoleContext.Provider value={{ flash, globalCommands: commands, registerStudioFrame }}>
      <div className="album-studio flex min-h-screen bg-[var(--as-paper)] text-[var(--as-ink)]">
        <SideNav pathname={location.pathname} onLogout={handleLogout} />

        <div className="flex min-w-0 flex-1 flex-col">
          <MobileTopBar navOpen={navOpen} onOpenNav={openNav} triggerRef={navTriggerRef} />
          <TopBar activeLabel={activeItem.label} readOnly={readOnly} hideCmdTrigger={studioFrameActive} onOpenCmd={() => setPaletteOpen(true)} />
          <main className="flex-1 overflow-auto px-[30px] pb-[60px] pt-[26px]">
            {/*
              The entrance animation is opacity-only ON PURPOSE. `as-fade-up`
              animates `transform`, and an element with a filled transform
              animation becomes the containing block for every `position: fixed`
              descendant — which trapped each surface's Drawer inside this
              1180px column instead of the viewport. On a page with little
              content the column is only a couple of hundred pixels tall, so the
              drawer's scroll body collapsed and its required fields (a release's
              date and artwork URL) could not be reached at all.
            */}
            <div key={location.pathname} className="mx-auto max-w-[1180px]" style={{ animation: 'as-fade .3s cubic-bezier(.22,1,.36,1) both' }}>
              {children}
            </div>
          </main>
        </div>

        <NavDrawer open={navOpen} onClose={closeNav} pathname={location.pathname} onLogout={handleLogout} />

        {/* Rendered null while a studio frame owns ⌘K, so its palette never stacks over the frame's. */}
        <CommandPalette open={paletteOpen && !studioFrameActive} onClose={() => setPaletteOpen(false)} commands={commands} />

        <ToastView host={toastHost} />
      </div>
    </ConsoleContext.Provider>
  )
}

/**
 * The Admin Console shell. Wraps the console subtree in the content-mode
 * provider (D3) and renders the Studio Console frame around `children` (the
 * router `<Outlet/>`).
 *
 * @param children - The active admin surface (from the router outlet).
 */
export default function AdminConsoleShell({ children }: { children: ReactNode }): JSX.Element {
  return (
    <ContentModeProvider>
      <ConsoleFrame>{children}</ConsoleFrame>
    </ContentModeProvider>
  )
}
